<template>
  <div v-if="showComponent">
    <div v-if="showLmEditorBubbleMenu" class="bubble-menu">
      <BubbleTextMenu
        class="bubble-menu-text"
        :editor="editorConfig"
        :hasVariables="!!variablesOptions.length"
        @toggle-variables="toggleVariables = !toggleVariables"
      />
    </div>
    <div v-if="editorConfig">
      <label
        v-if="label"
        :for="idComputed"
        :title="titleComputed"
        :class=" {'col-sm-2': isOnModeLabelOnLeft, 'col-form-label': isOnModeLabelOnLeft }"
        class="text-truncate"
      >
        {{label}}
        <small v-if="required">*</small>
      </label>
      <font-awesome-icon
        id="font-awesome-icon-edit"
        icon="edit"
        size="sm"
        class="clickable float-right mt-1"
        :class="{ 'ml-2': help }"
        v-if="showEditButton"
        @click="$emit('edit-button-clicked')"
      />
      <lm-dropdown
        v-if="isEditableByPickedUser"
        class="float-right"
        :tooltip="!disableEditors ? 'Selecciona quién edita este campo' : null"
        template="user-list"
        @input="$emit('editor-change', $event)"
        :value="collaborator"
        :disabled="disableEditors"
        closeAfterPick
      >
        <div class="d-flex align-items-center gap-2">
          {{ collaborator.name || '' }}
          <a-icon type="edit" v-if="!disableEditors" />
        </div>
      </lm-dropdown>
      <font-awesome-icon
        icon="question-circle"
        size="sm"
        class="clickable float-right mt-1"
        v-if="help"
        v-b-tooltip.hover="help"
      />

      <template v-if="!disabled && !hideMenus">
        <TextMenu
          :editor="editorConfig"
          :hasVariables="!!variablesOptions.length"
          @toggle-table-settings="toggleTableSettings = !toggleTableSettings"
          @toggle-variables="toggleVariables = !toggleVariables"
          @add-image="addImage"
        />

        <TableMenu :editor="editorConfig" v-if="toggleTableSettings" />

        <LmTreeSelect
          class="mt-3"
          v-if="variablesOptions.length && toggleVariables"
          :options="variablesOptions"
          placeholder="Insertar variable"
          @input="onInsertVariable"
        />
      </template>

      <editor-content :editor="editorConfig" />

        <LmVoiceRecognition
          :value="value"
          :enableVoiceRecognition="enableVoiceRecognition"
          @onVoiceRecognitionResult="onVoiceRecognitionResult"
        />
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import Mention from '@tiptap/extension-mention'
import TextAlign from '@tiptap/extension-text-align'
import Placeholder from '@tiptap/extension-placeholder'
import BubbleMenu from '@tiptap/extension-bubble-menu'
import Underline from '@tiptap/extension-underline'
import Image from '@tiptap/extension-image'

// Components
import suggestion from './utils/suggestion'
import { Indent } from './utils/indent'
import TableMenu from './components/TableMenu'
import TextMenu from './components/TextMenu'
import BubbleTextMenu from './components/BubbleTextMenu'
import LmTreeSelect from '@/components/form/elements/LmTreeSelect'
import LmVoiceRecognition from '@/components/form/elements/LmVoiceRecognition'

// Utils
import lmInputMixin from '@/mixins/lmInputMixin.js'

const CustomTableCell = TableCell.extend({
  addAttributes () {
    return {
      // extend the existing attributes …
      ...this.parent?.(),

      // and add a new one …
      backgroundColor: {
        default: null,
        parseHTML: element => element.getAttribute('data-background-color'),
        renderHTML: attributes => {
          return {
            'data-background-color': attributes.backgroundColor,
            style: `background-color: ${attributes.backgroundColor}`
          }
        }
      }
    }
  }
})

export default {
  mixins: [lmInputMixin],
  components: {
    EditorContent,
    TableMenu,
    TextMenu,
    BubbleTextMenu,
    LmTreeSelect,
    BubbleMenu,
    LmVoiceRecognition
  },
  props: {
    variablesOptions: {
      type: Array,
      default: () => []
    },
    useMentions: {
      type: Boolean,
      default: false
    },
    scrollable: {
      type: Boolean,
      default: false
    },
    hideMenus: {
      type: Boolean,
      default: false
    },
    editorHeight: {
      type: Number,
      default: 0
    },
    placeholder: {
      type: String,
      default: 'Escribe un texto aquí...'
    },
    // Propiedad de contenido por retrocompatibilidad
    contentProps: {
      type: String,
      default: null
    },
    enableVoiceRecognition: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      editorConfig: null,
      toggleTableSettings: false,
      toggleVariables: false
    }
  },
  computed: {
    data () {
      return this.value || this.contentProps || ''
    },
    editorClassList () {
      let classList = 'lm-editor-core'
      if (this.scrollable) {
        classList += ' scroll'
      }

      return classList
    },
    showLmEditorBubbleMenu () {
      return process.env?.VUE_APP_SHOW_LMEDITOR_BUBBLE_MENU === 'yes'
    }
  },
  watch: {
    scrollable (val) {
      this.editorConfig.setOptions({
        editorProps: {
          attributes: {
            class: val ? 'lm-editor-core scroll' : 'lm-editor-core'
          }
        }
      })
    },
    disabled (val) {
      this.editorConfig.setEditable(!val)
      this.editorConfig.setOptions({
        editorProps: {
          attributes: {
            class: val ? 'lm-editor-core disabled' : 'lm-editor-core'
          }
        }
      })
    },
    value (val) {
      const isSame = this.editorConfig.getHTML() === val
      if (isSame) {
        return
      }
      this.editorConfig.commands.setContent(val, false)
    }
  },
  methods: {
    addImage () {
      const url = window.prompt('URL')

      if (url) {
        this.editorConfig.chain().focus().setImage({ src: url }).run()
      }
    },
    onInsertVariable (event) {
      this.editorConfig.commands.insertContent(`{{ ${event.value} }}`)
    },
    getEditorMentions (content) {
      const html = document.createElement('div')
      html.innerHTML = content
      const mentionsTags = html.querySelectorAll('[data-type="mention"]')

      return Array.from(mentionsTags).map(item => ({ id: item.dataset.id, name: item.dataset.label }))
    },
    onVoiceRecognitionResult (newValue) {
      this.$emit('input', newValue) // Añadir el texto reconocido al texto existente
    }
  },
  mounted () {
    const editorConfig = {
      editorProps: {
        attributes: {
          class: this.editorClassList
        }
      },
      extensions: [
        StarterKit,
        Table.configure({
          resizable: true
        }),
        BubbleMenu.configure({
          element: document.querySelector('.bubble-menu')
        }),
        TableRow,
        TableHeader,
        CustomTableCell,
        TextAlign.configure({
          types: ['heading', 'paragraph'],
          alignments: ['left', 'center', 'right', 'justify']
        }),
        Placeholder.configure({
          placeholder: this.placeholder
        }),
        Underline,
        Indent,
        Image
      ],
      content: this.data,
      onUpdate: () => {
        const htmlContent = this.editorConfig.getHTML()
        const mentions = this.getEditorMentions(htmlContent)
        this.$emit('input', htmlContent)
        this.$emit('mentions-list', mentions)
        // Añadado este evento por razones de retrocompabilidad
        this.$emit('onUpdate', htmlContent)
      }
    }

    if (this.useMentions) {
      editorConfig.extensions.push(
        Mention.configure({
          HTMLAttributes: {
            class: 'mention'
          },
          suggestion
        })
      )
    }

    if (this.editorHeight) {
      editorConfig.editorProps.attributes = {
        class: this.editorClassList,
        style: `height: ${this.editorHeight}px; min-height: ${this.editorHeight}px;`
      }
    }

    this.editorConfig = new Editor(editorConfig)

    if (this.disabled) {
      this.editorConfig.setEditable(false)
      this.editorConfig.setOptions({
        editorProps: {
          attributes: {
            class: this.disabled ? 'lm-editor-core disabled' : 'lm-editor-core'
          }
        }
      })
    }
  },
  beforeDestroy () {
    this.editorConfig.destroy()
  }
}
</script>

<style>
  .lm-editor-core {
  min-height: 300px;
  padding: 1.5rem;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
}

.lm-editor-core.disabled {
  background-color: #e9ecef;
  cursor: not-allowed;
}

.lm-editor-core.scroll {
  overflow-y: auto;
  max-height: 300px;
}

.lm-editor-core.scroll:hover::-webkit-scrollbar-thumb {
  border-radius: 20px;
  background: #888;
}

.lm-editor-core.scroll::-webkit-scrollbar {
  width: 8px;
}

.lm-editor-core.scroll::-webkit-scrollbar-thumb {
  border-radius: 20px;
  background: transparent;
}

.lm-editor-core.scroll::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.lm-editor-core:focus {
  outline: 0;
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.lm-editor-core ul li {
  list-style: disc;
}

.ProseMirror {
  margin: 1rem 0;
}

.ProseMirror > * + * {
  margin-top: 0.75em;
}

.ProseMirror ul,
.ProseMirror ol {
  padding: 0 1rem;
}

.ProseMirror h1,
.ProseMirror h2,
.ProseMirror h3,
.ProseMirror h4,
.ProseMirror h5,
.ProseMirror h6 {
  line-height: 1.1;
}

.ProseMirror pre {
  background: #363636;
  color: #fff;
  font-family: Fira code, Fira Mono, Consolas, Menlo, Courier, monospace;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
}

.ProseMirror pre code {
  color: inherit;
  padding: 0;
  background: none;
  font-size: 1rem;
}

.ProseMirror img {
  max-width: 100%;
  height: auto;
}

.ProseMirror blockquote {
  padding-left: 1rem;
  border-left: 2px solid rgba(13, 13, 13, 0.1);
}

.ProseMirror hr {
  border: none;
  border-top: 2px solid rgba(13, 13, 13, 0.1);
  margin: 2rem 0;
}

.ProseMirror p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: #adb5bd;
  pointer-events: none;
  height: 0;
}

.ProseMirror table {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
  margin: 0;
  overflow: hidden;
}

.ProseMirror table td,
.ProseMirror table th {
  min-width: 1em;
  border: 2px solid #ced4da;
  padding: 3px 5px;
  vertical-align: top;
  box-sizing: border-box;
  position: relative;
}

.ProseMirror table td > * {
  margin-bottom: 0;
}

.ProseMirror table th > * {
  margin-bottom: 0;
}

.ProseMirror table th {
  font-weight: bold;
  text-align: left;
  background-color: #f1f3f5;
}

.ProseMirror table .selectedCell:after {
  z-index: 2;
  position: absolute;
  content: "";
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: rgba(200, 200, 255, 0.4);
  pointer-events: none;
}

.ProseMirror table .column-resize-handle {
  position: absolute;
  right: -2px;
  top: 0;
  bottom: -2px;
  width: 4px;
  background-color: #adf;
  pointer-events: none;
}

.ProseMirror table p {
  margin: 0;
}

.tableWrapper {
  overflow-x: auto;
}

.resize-cursor {
  cursor: ew-resize;
  cursor: col-resize;
}

.mention {
  color: #007bff;
}

.bubble-menu {
  min-width: 600px;
  background: white;
}

</style>
